import React, { useEffect, useState, useRef } from 'react';
import RenderAnimation from '../render-animation/RenderAnimation';
import { isDomNodeType } from '../../utils/misc';
import c from './loading-screen.module.scss';
import { getRandomIntInclusive } from '../../utils/random';
import { isStringEmpty } from '../../utils/string';
import { fsGetDoc } from '../../firebase/firestore';

// Get a randomised loading message based on the following logic:
// Post login buffer is classified as the period between gaining okta auth and signing in...
// stage 0: show random loading phrase
// stage 1: show random fun fact prefixed with "Fun facts!"
// stage 2: show random fun fact (no prefix)
// then loop stage 2
const getRandomLoadingMessage = (stage, LOADING_PHRASES, FUN_FACTS) => {
  const prebufferPause =
    stage === 0 && sessionStorage.getItem('postLoginBuffer') === 'true';
  if (sessionStorage.getItem('postLoginBuffer') === 'false') {
    return 'Logging you in now...';
  }
  // might need or if stage 0 in ehre
  if (!LOADING_PHRASES || !FUN_FACTS || prebufferPause) {
    return '';
  } else {
    const messagesArray = stage === 0 ? LOADING_PHRASES : FUN_FACTS;
    const randomIndex = getRandomIntInclusive(0, messagesArray.length - 1);
    const rawMessage = messagesArray[randomIndex];
    return stage === 1 ? `Fun facts! ${rawMessage}` : rawMessage;
  }
};

const LoadingScreen = ({
  children,
  message = null, // Optionally override with custom message. Either supply a single string, or an array of strings which will loop in order
  screenReaderMessage = 'Loading and verifying credentials.',
  loaded = false,
  style,
  animation,
}) => {
  const [mount, setMount] = useState(false);
  const [stage, setStage] = useState(0);
  const [loadingData, setLoadingData] = useState({});
  const singleMessageSupplied = !isStringEmpty(message); // single string
  const multipleMessagesSupplied = Array.isArray(message) && message.length > 0; // array

  useEffect(() => {
    fsGetDoc('appResources/loadingScreenMisc').then((r) =>
      setLoadingData(r.data()),
    );
  }, []);

  const LOADING_PHRASES = loadingData.LOADING_PHRASES;
  const FUN_FACTS = loadingData.FUN_FACTS;

  useEffect(() => {
    if (loaded) {
      setTimeout(() => setMount(true), 100);
    } else {
      setMount(false);
    }
  }, [loaded]);

  useEffect(() => {
    if (singleMessageSupplied) return;

    const INTERVAL = 3000;
    const interval = setInterval(() => {
      setStage((p) => p + 1);
    }, INTERVAL);

    return () => clearInterval(interval);
  }, []);

  // initial mount focus for screen reader AX
  const containerRef = useRef(null);
  useEffect(() => {
    if (isDomNodeType(containerRef.current)) {
      containerRef.current.focus();
    }
  }, [containerRef.current]);

  // allow overriding with custom loading message(s)
  let loadingMessage;
  if (singleMessageSupplied) {
    loadingMessage = message;
  } else if (multipleMessagesSupplied) {
    loadingMessage = message[stage % message.length];
  } else {
    loadingMessage = getRandomLoadingMessage(stage, LOADING_PHRASES, FUN_FACTS);
  }

  return (
    <>
      {mount ? (
        children
      ) : (
        <div
          ref={containerRef}
          className={c.loadingScreen}
          style={{ ...style, opacity: loaded ? 0 : 1 }}
          tabIndex={-1}
          aria-label={screenReaderMessage}
        >
          <h1 className="virtuallyHidden">Loading Screen:</h1>
          <div className={c.animationWidth}>
            <RenderAnimation
              className={c.animationWidth}
              lottieFile={animation}
              aspectRatio="xMinYMid meet"
              loop={true}
            />
            <div
              key={stage} // added key to force react to rerender the message to ensure the fade animation is kept in sync
              className={`${c.message} ${singleMessageSupplied ? null : c.fade}`}
            >
              {loadingMessage}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LoadingScreen;
